import { useState, useEffect, useRef } from "react";
import { Plus, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";

interface CreateCaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreateCase: (caseData: { title: string; type: string; urls: string[] }) => Promise<void>;
}

interface FormData {
  title: string;
  type: string;
  urlsText: string;
}

interface FormErrors {
  title?: string;
  type?: string;
  urls?: string;
}

const RANDOM_CASE_NAMES = [
  "NIGHTFALL",
  "CROSSWIND",
  "STARLIGHT",
  "THUNDERBOLT",
  "DARKWATER",
  "FIREWALL",
  "BLACKOUT",
  "BLUEBIRD",
  "GOLDFINCH",
  "SILVERSTONE",
  "REDWOOD",
  "CLEARWATER",
  "GRANITE",
  "WINDSTORM",
  "IRONCLAD",
  "SENTINEL",
  "LIGHTHOUSE",
  "SAFEGUARD",
  "KEYSTONE",
  "FORTRESS"
];

const generateCaseName = (): string => {
  const randomIndex = Math.floor(Math.random() * RANDOM_CASE_NAMES.length);
  return RANDOM_CASE_NAMES[randomIndex];
};

export function CreateCaseModal({ isOpen, onClose, onCreateCase }: CreateCaseModalProps) {
  const [formData, setFormData] = useState<FormData>({
    title: "",
    type: "",
    urlsText: ""
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const hasGeneratedTitle = useRef(false);

  // Generate a random case name when modal opens
  useEffect(() => {
    if (isOpen && !hasGeneratedTitle.current) {
      setFormData(prev => ({
        ...prev,
        title: generateCaseName()
      }));
      hasGeneratedTitle.current = true;
    } else if (!isOpen) {
      hasGeneratedTitle.current = false;
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        title: "",
        type: "",
        urlsText: ""
      });
      setErrors({});
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = "Case title is required";
    }

    if (!formData.type) {
      newErrors.type = "Case type must be selected";
    }

    if (!formData.urlsText.trim()) {
      newErrors.urls = "At least one URL is required";
    } else {
      // Validate URLs
      const urls = formData.urlsText
        .split('\n')
        .map(url => url.trim())
        .filter(url => url);

      if (urls.length === 0) {
        newErrors.urls = "At least one URL is required";
      } else {
        const invalidUrls = urls.filter(url => {
          try {
            new URL(url);
            return false;
          } catch {
            return true;
          }
        });

        if (invalidUrls.length > 0) {
          newErrors.urls = `Invalid URLs: ${invalidUrls.slice(0, 3).join(', ')}${invalidUrls.length > 3 ? '...' : ''}`;
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Extract URLs from textarea
    const urls = formData.urlsText
      .split('\n')
      .map(url => url.trim())
      .filter(url => url);

    try {
      // Call the async onCreateCase function
      await onCreateCase({
        title: formData.title,
        type: formData.type,
        urls
      });
      
      // Close modal - form reset will happen automatically via useEffect
      handleClose();
    } catch (error) {
      console.error('Error creating case:', error);
      // Error handling is already done in the parent component
      setIsSubmitting(false);
    }
  };

  const handleGenerateNewName = () => {
    setFormData(prev => ({
      ...prev,
      title: generateCaseName()
    }));
  };

  const handleUrlsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const currentText = e.target.value;
    
    // Auto-format: Split by any whitespace and filter out empty entries
    const urls = currentText
      .split(/\s+/)
      .map(url => url.trim())
      .filter(url => url.length > 0);
    
    // Join with line breaks (auto-format)
    const formatted = urls.length > 0 ? urls.join('\n') : currentText;
    
    setFormData(prev => ({ ...prev, urlsText: formatted }));
  };

  // Custom close handler to prevent state updates during transition
  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  const getCaseTypeDescription = (type: string): string => {
    const descriptions: Record<string, string> = {
      "ncii": "Non-consensual intimate images investigation",
      "verified-csam": "Verified child sexual abuse material - confirmed illegal content requiring immediate action",
      "suspected-csam": "Suspected child sexual abuse material - content requiring forensic analysis and verification",
      "verified-trafficking": "Confirmed human trafficking investigation",
      "test-case": "Test case for training and development purposes"
    };
    return descriptions[type] || "";
  };

  const getCaseTypeDisplayName = (type: string): string => {
    const displayNames: Record<string, string> = {
      "ncii": "NCII",
      "verified-csam": "Verified CSAM",
      "suspected-csam": "Suspected CSAM",
      "verified-trafficking": "Verified Human Trafficking",
      "test-case": "Test Case"
    };
    return displayNames[type] || "";
  };

  const getUrlCount = (): number => {
    if (!formData.urlsText.trim()) return 0;
    return formData.urlsText
      .split('\n')
      .map(url => url.trim())
      .filter(url => url).length;
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create New Case</DialogTitle>
          <DialogDescription>
            Set up a new investigation case with target URLs for analysis. Provide a descriptive title, select the case type, and enter the URLs you want to investigate.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          {/* Case Title */}
          <div className="space-y-2">
            <Label htmlFor="title">Case Title *</Label>
            <div className="flex gap-2">
              <Input
                id="title"
                placeholder="Enter case title..."
                value={formData.title}
                onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                className={`flex-1 ${errors.title ? 'border-red-400 focus:border-red-400' : ''}`}
                aria-invalid={!!errors.title}
                aria-describedby={errors.title ? 'title-error' : 'title-help'}
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={handleGenerateNewName}
                className="px-3"
                title="Generate new codename"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
            {errors.title ? (
              <p id="title-error" className="text-sm text-red-500" role="alert">
                {errors.title}
              </p>
            ) : (
              <p id="title-help" className="text-sm text-muted-foreground">
                Auto-generated codename • Click refresh to generate a new one
              </p>
            )}
          </div>

          {/* Case Type */}
          <div className="space-y-2">
            <Label htmlFor="type">Case Type *</Label>
            <Select
              value={formData.type}
              onValueChange={(value) => setFormData(prev => ({ ...prev, type: value }))}
            >
              <SelectTrigger 
                className={errors.type ? 'border-red-400 focus:border-red-400' : ''}
                aria-invalid={!!errors.type}
                aria-describedby={errors.type ? 'type-error' : formData.type ? 'type-description' : undefined}
              >
                <SelectValue placeholder="Select case type...">
                  {formData.type ? getCaseTypeDisplayName(formData.type) : "Select case type..."}
                </SelectValue>
              </SelectTrigger>
              <SelectContent 
                className="no-animations"
                position="item-aligned"
                side="bottom" 
                align="start"
                avoidCollisions={false}
                sticky="always"
                style={{
                  animation: 'none !important',
                  transition: 'none !important',
                  transform: 'none !important'
                }}
              >
                <SelectItem value="ncii">
                  <div className="flex flex-col items-start">
                    <span>NCII</span>
                    <span className="text-xs text-muted-foreground">Non-consensual intimate images</span>
                  </div>
                </SelectItem>
                <SelectItem value="verified-csam">
                  <div className="flex flex-col items-start">
                    <span>Verified CSAM</span>
                    <span className="text-xs text-muted-foreground">Confirmed child sexual abuse material</span>
                  </div>
                </SelectItem>
                <SelectItem value="suspected-csam">
                  <div className="flex flex-col items-start">
                    <span>Suspected CSAM</span>
                    <span className="text-xs text-muted-foreground">Potential child sexual abuse material</span>
                  </div>
                </SelectItem>
                <SelectItem value="verified-trafficking">
                  <div className="flex flex-col items-start">
                    <span>Verified Human Trafficking</span>
                    <span className="text-xs text-muted-foreground">Confirmed human trafficking operations</span>
                  </div>
                </SelectItem>
                <SelectItem value="test-case">
                  <div className="flex flex-col items-start">
                    <span>Test Case</span>
                    <span className="text-xs text-muted-foreground">Testing and training purposes</span>
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            {errors.type ? (
              <p id="type-error" className="text-sm text-red-500" role="alert">
                {errors.type}
              </p>
            ) : formData.type ? (
              <p id="type-description" className="text-sm text-muted-foreground">
                {getCaseTypeDescription(formData.type)}
              </p>
            ) : null}
          </div>

          {/* Target URLs */}
          <div className="space-y-2">
            <Label htmlFor="urls">Target URLs *</Label>
            <Textarea
              id="urls"
              placeholder={`https://example.com/page1
https://example.com/page2
https://example.com/page3

Enter one URL per line...`}
              value={formData.urlsText}
              onChange={handleUrlsChange}
              className={`min-h-[100px] font-mono text-sm ${errors.urls ? 'border-red-400 focus:border-red-400' : ''}`}
              rows={5}
              aria-invalid={!!errors.urls}
              aria-describedby={errors.urls ? 'urls-error' : 'urls-help'}
            />
            {errors.urls ? (
              <p id="urls-error" className="text-sm text-red-500" role="alert">
                {errors.urls}
              </p>
            ) : (
              <div className="flex justify-between text-sm text-muted-foreground">
                <span>Enter or paste URLs • Auto-formats to one per line</span>
                <span>{getUrlCount()} URLs</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  Create Case
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}